import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { bootstrapProfile } from '../../lib/onboarding';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        if (!session?.user) {
          // If there's no session but also no error in the URL, wait for Supabase to handle the OAuth response
          if (!location.search.includes('error')) {
            return;
          }
          throw new Error('Authentication failed');
        }

        // Bootstrap (or fetch) the caller's profile via a SECURITY DEFINER
        // RPC - the old direct organizations/profiles inserts could never
        // pass RLS for a first-time user (org policy requires an existing
        // profile), which dead-ended every first OAuth login here. Joins a
        // pending team invite's org instead of a fresh one when one is
        // waiting (see lib/onboarding.ts).
        const { error: provisionError } = await bootstrapProfile();
        if (provisionError) throw provisionError;

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (err) {
        console.error('Error in OAuth callback:', err);
        setError(err instanceof Error ? err.message : 'Failed to complete authentication');
      }
    };

    handleCallback();
  }, [navigate, location]);

  // Check for error in URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlError = params.get('error_description');
    if (urlError) {
      setError(decodeURIComponent(urlError));
    }
  }, [location]);

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Please try logging in again or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh'
    }}>
      <CircularProgress />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Setting up your account...
      </Typography>
    </Box>
  );
};

export default OAuthCallback;
